import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const isHttpProtocol = (u: URL) => u.protocol === "http:" || u.protocol === "https:";
const isSameOrigin = (u: URL) => u.origin === window.location.origin;

const isStaticFile = (pathname: string) => {
  return /\.(pdf|zip|doc|docx|xls|xlsx|jpg|jpeg|png|gif|svg|mp4|mp3|txt|csv)$/i.test(pathname);
};

export const TopWindowNavEnforcer: React.FC<{ scrollToTop?: boolean }> = ({ scrollToTop = true }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!scrollToTop) return;
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      // ignore
    }
  }, [location.pathname, scrollToTop]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || (e as any).button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const target = (e.target as Element | null)?.closest?.("a[href]");
      if (!target) return;

      const a = target as HTMLAnchorElement;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("#") || a.hasAttribute("download")) return;

      if (a.getAttribute("target") === "_blank") return;

      try {
        const url = new URL(href, window.location.origin);
        if (!isHttpProtocol(url)) return;
        if (!isSameOrigin(url)) return;

        if (isStaticFile(url.pathname)) return;

        e.preventDefault();
        const relativeUrl = url.pathname + url.search + url.hash;
        navigate(relativeUrl);
      } catch {
        // невалидный URL
      }
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [navigate]);

  useEffect(() => {
    const ensureExternalTargets = () => {
      document.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((a) => {
        const href = a.getAttribute("href");
        if (!href || href.startsWith("#") || a.hasAttribute("download")) return;
        try {
          const url = new URL(href, window.location.origin);
          if (!isHttpProtocol(url)) return;
          if (!isSameOrigin(url)) {
            if (!a.getAttribute("target")) a.setAttribute("target", "_blank");
            const rel = (a.getAttribute("rel") || "").split(/\s+/).filter(Boolean);
            if (!rel.includes("noopener")) rel.push("noopener");
            if (!rel.includes("noreferrer")) rel.push("noreferrer");
            a.setAttribute("rel", rel.join(" "));
          }
        } catch {
          // skip bad URLs
        }
      });
    };

    ensureExternalTargets();
    const mo = new MutationObserver(ensureExternalTargets);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["href"] });
    return () => mo.disconnect();
  }, []);

  return null;
};
